import type { IconName } from '@/design-system';
import {
    hasSurvey,
    reportStageReached,
    sentMilestones,
    surveyOnDevice,
    type Order,
} from '@/entities/order';
import { isPlatformAdmin, type Session } from '@/shared/api';

import { paths } from '../router/paths';
import { orderLayoutCopy } from './copy';

/**
 * De tabbladen van één keuring, als gegevens.
 *
 * De rail links is voor wat altijd bestaat en is per rol altijd hetzelfde. Wat
 * bij één keuring hoort, staat hier: in de kop van die keuring, en alleen wat
 * deze keuring heeft.
 *
 * - **Overzicht** - altijd.
 * - **Opname** - per dienst met een opname met vragenlijst, zolang die opname op
 *   een apparaat staat (ingepland of in opname). Heeft de keuring er meer dan
 *   één, dan noemt het tabblad de dienst. De NHG-bijlage en een dienst zonder
 *   vragenlijst hebben geen opname: daar valt niets in te vullen.
 * - **Energielabel** - alleen bij de dienst NTA 8800 met zo'n opname, onder
 *   dezelfde voorwaarde.
 * - **Rapport** - altijd; zonder rapport zegt het scherm wanneer het komt.
 * - **Klantpagina** - zodra de koper een versie heeft.
 *
 * Dat volgt de knoppen die er al waren: "Opname openen" stond in het dossier
 * alleen zolang de opname op het apparaat staat, "Klantpagina" in de detailbalk
 * pas na het versturen. Een tabblad dat gegarandeerd op "staat niet op dit
 * apparaat" uitkomt, hoort er niet. Wat van het apparaat of de sessie afhangt
 * (offline, meekijken) blijft staan: dat legt het scherm zelf uit.
 *
 * Het tabblad van het scherm dat openstaat staat er altijd, ook als er niets te
 * zien is - anders weet je niet meer waar je bent.
 */
export type OrderTabKind = 'overview' | 'survey' | 'energyLabel' | 'report' | 'clientPage';

export interface OrderTab {
    /** Uniek in de rij: een keuring met twee opnames heeft twee `survey`-tabbladen. */
    key: string;
    kind: OrderTabKind;
    label: string;
    to: string;
    /** Het lijn-icoon van het merkblad, hetzelfde als toen deze schermen in de rail stonden. */
    icon: IconName;
}

/** Wat deze sessie van een keuring mag inzien. */
export interface OrderTabAccess {
    /** Opname en energielabel (`inspections.view`). */
    inspections: boolean;
    /** Rapport en klantpagina (`reports.view`). */
    reports: boolean;
}

export interface OrderTabContext {
    /** De uuid uit het adres; de tabbladen Overzicht, Rapport en Klantpagina gebruiken hem. */
    orderUuid: string;
    /** `null` zolang de keuring laadt, of als ze niet te laden was. */
    order: Order | null;
    access: OrderTabAccess;
    /** Heeft de koper een versie van het rapport? `null` zolang dat niet bekend is. */
    released: boolean | null;
    /** Het tabblad van het scherm dat openstaat. */
    current: OrderTabKind | null;
}

const copy = orderLayoutCopy.tabs;

export function buildOrderTabs({
    orderUuid,
    order,
    access,
    released,
    current,
}: OrderTabContext): OrderTab[] {
    const tabs: OrderTab[] = [
        {
            key: 'overview',
            kind: 'overview',
            label: copy.overview,
            to: paths.dossier(orderUuid),
            icon: 'dossiers',
        },
    ];

    if (order && access.inspections && surveyOnDevice(order)) {
        const withInspection = order.services.filter(hasSurvey);
        const surveys = withInspection.filter((service) => service.service !== 'energielabel');
        const energyLabel = withInspection.find((service) => service.service === 'energielabel');

        for (const service of surveys) {
            tabs.push({
                key: `survey-${service.inspection.uuid}`,
                kind: 'survey',
                label: surveys.length === 1 ? copy.survey : copy.surveyFor(service.name),
                to: paths.survey(service.inspection.uuid),
                icon: 'survey',
            });
        }

        if (energyLabel) {
            tabs.push({
                key: 'energy-label',
                kind: 'energyLabel',
                label: copy.energyLabel,
                to: paths.energyPerformance(energyLabel.inspection.uuid),
                icon: 'energyLabel',
            });
        }
    }

    if (access.reports || current === 'report') {
        tabs.push({
            key: 'report',
            kind: 'report',
            label: copy.report,
            to: paths.report(orderUuid),
            icon: 'reports',
        });
    }

    if ((access.reports && hasClientPage(order, released)) || current === 'clientPage') {
        tabs.push({
            key: 'client-page',
            kind: 'clientPage',
            label: copy.clientPage,
            to: paths.clientPage(orderUuid),
            icon: 'clientPage',
        });
    }

    return tabs;
}

/**
 * Moet de kop vragen of de koper een versie heeft? Alleen als er een rapport
 * kan zijn en deze sessie het mag zien.
 */
export function needsReleaseCheck(order: Order | null, access: OrderTabAccess): boolean {
    return order !== null && access.reports && reportStageReached(order);
}

/**
 * De permissies uit de sessie zijn leidend; ontbreken ze (een oudere sessie),
 * dan mag elke rol in een bedrijf keuringen, opnames en rapporten inzien. Een
 * platformbeheerder die meekijkt komt overal binnen, net als bij `hasRole`: de
 * permissies in die sessie gaan over het platform, niet over dit bedrijf.
 */
export function orderTabAccessFor(
    session: Pick<Session, 'user' | 'roles' | 'permissions'> | null,
): OrderTabAccess {
    if (!session) {
        return { inspections: false, reports: false };
    }

    if (isPlatformAdmin(session)) {
        return { inspections: true, reports: true };
    }

    const may = (permission: string) =>
        session.permissions.length === 0 || session.permissions.includes(permission);

    return { inspections: may('inspections.view'), reports: may('reports.view') };
}

/**
 * De koper heeft een versie zodra er een is goedgekeurd. Zolang het antwoord
 * daarop er niet is, zegt de status het: een afgeronde keuring is verstuurd.
 */
function hasClientPage(order: Order | null, released: boolean | null): boolean {
    if (released !== null) {
        return released;
    }

    return order !== null && sentMilestones(order).report;
}
